import React from 'react';
import { Share2 } from 'lucide-react';
import { formatArticleDate } from '../../data/articles';

interface ArticleCardProps {
  title: string;
  excerpt: string;
  image: string;
  publishDate: string;
  tags: string[];
  slug: string;
  url?: string;
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  title,
  excerpt,
  image,
  publishDate,
  tags,
  slug,
  url,
}) => {
  const handleShare = async () => {
    try {
      await navigator.share({
        title,
        text: excerpt,
        url: url || `/blog/${slug}`,
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <article 
      className="bg-white rounded-lg overflow-hidden shadow-custom hover:shadow-custom-hover transition-all duration-300"
      itemScope
      itemType="http://schema.org/BlogPosting"
    >
      <meta itemProp="datePublished" content={publishDate} />
      {tags.map(tag => (
        <meta key={tag} itemProp="keywords" content={tag} />
      ))}
      
      <div className="relative aspect-[16/9] overflow-hidden">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
          loading="lazy"
          itemProp="image"
        />
      </div>
      
      <div className="p-6">
        <div className="flex items-center justify-end mb-3">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleShare();
            }}
            className="p-2 hover:bg-light-gray rounded-full transition-colors duration-200"
            aria-label="Share article"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
        
        <h3 
          className="font-abadi text-xl font-medium mb-3"
          itemProp="headline"
        >
          {title}
        </h3>
        
        <p 
          className="text-dark-gray mb-4"
          itemProp="description"
        >
          {excerpt}
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {tags.map(tag => (
            <span 
              key={tag}
              className="text-xs bg-light-gray px-2 py-1 rounded-full text-dark-gray"
            >
              {tag}
            </span>
          ))}
        </div>
        
        <div className="flex justify-end">
          <span className="text-sm text-dark-gray">{formatArticleDate(publishDate)}</span>
        </div>
      </div>
    </article>
  );
};

export default ArticleCard;